import * as React from 'react';

export const DEFAULT_CLAMP_COLLAPSED_LABEL = 'Show more';
export const DEFAULT_CLAMP_EXPANDED_LABEL = 'Show less';

export interface ClampTriggerContextValue {
  label: string;
  expanded: boolean;
  contentId: string;
  onToggle: () => void;
}

const ClampTriggerContext =
  React.createContext<ClampTriggerContextValue | null>(null);

export interface ClampTriggerRegistration {
  register: () => void;
  unregister: () => void;
}

const ClampTriggerRegistrationContext =
  React.createContext<ClampTriggerRegistration | null>(null);

/** `useLayoutEffect` in the browser; `useEffect` on the server to avoid SSR warnings. */
export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export const ClampTriggerContextProvider = ClampTriggerContext.Provider;
export const ClampTriggerRegistrationProvider =
  ClampTriggerRegistrationContext.Provider;

export const useClampTriggerContext = (): ClampTriggerContextValue | null => {
  const value = React.useContext(ClampTriggerContext);
  const registration = React.useContext(ClampTriggerRegistrationContext);

  useIsomorphicLayoutEffect(() => {
    if (!value || !registration) return;
    registration.register();
    return () => registration.unregister();
  }, [registration, value]);

  return value;
};

export const resolveClampContentIds = ({
  asChild,
  children,
  id,
  contentId,
  showTrigger,
}: {
  asChild?: boolean;
  children: React.ReactNode;
  id?: string;
  contentId: string;
  showTrigger: boolean;
}): { triggerContentId: string; renderedContentId: string | undefined } => {
  const childId =
    asChild &&
    React.isValidElement(children) &&
    typeof (children.props as { id?: unknown }).id === 'string'
      ? (children.props as { id: string }).id
      : undefined;

  return {
    triggerContentId: childId ?? id ?? contentId,
    renderedContentId: childId ?? id ?? (showTrigger ? contentId : undefined),
  };
};
